import { PageHeader, Tabs, Typography } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Cell, Grid } from 'styled-css-grid';
import { BackButton } from '../buttons/BackButton/BackButton';
import Spinner from '../lib/Antd/Spinner';
import enResource from '../locales/en/Resources.json';
import fiResource from '../locales/fi/Resources.json';
import plResource from '../locales/pl/Resources.json';
import svResource from '../locales/sv/Resources.json';
import SettingsTabHeader from '../SettingsTabHeader/SettingsTabHeader';

const { Paragraph } = Typography;
const { TabPane } = Tabs;

const Header = styled(Cell)`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const StyledParagraph = styled(Paragraph)`
  &&& {
    margin: 0px;
  }
`;

const StyledPageHeader = styled(PageHeader)`
  &&& {
    padding: 0px;
  }
  &&& .ant-page-header-heading {
    display: flex;
    align-items: center;
  }
  &&& .ant-page-header-content {
    padding: 0px;
  }
`;

const StyledGrid = styled(Grid)`
  background-color: white;
  border: 1px solid #e8e8e8;
  padding: 15px 30px;
  height: 100%;
  width: 100%;
`;

const StyledTabs = styled(Tabs)`
  height: 100%;

  .ant-tabs-content {
    height: 100%;
  }
`;

export interface SettingTab {
  identification: string;
  tabName: string;
  Content: ({ identification }: { identification: string }) => JSX.Element;
  tabText: string;
}

export interface SettingsContainerProps {
  settingTabs: SettingTab[];
  loadSettings: (identification: string) => Promise<void>;
  hasUnsavedChanges: (identification: string) => boolean;
  resetSettings: () => void;
  onGoBack: () => void;
  title: string;
}

export const SettingsContainer = ({
  settingTabs,
  loadSettings,
  hasUnsavedChanges,
  resetSettings,
  onGoBack,
  title
}: SettingsContainerProps) => {
  const i18n = useTranslation()[1];
  i18n.addResources('en', 'geosecma-settings', enResource.settings);
  i18n.addResources('sv', 'geosecma-settings', svResource.settings);
  i18n.addResources('fi', 'geosecma-settings', fiResource.settings);
  i18n.addResources('pl', 'geosecma-settings', plResource.settings);
  const { t } = useTranslation('geosecma-settings');

  const [activeTab, setActiveTab] = React.useState<SettingTab>(settingTabs[0]);
  const [tabLoading, setTabLoading] = React.useState(true);
  const [popConfirmVisibility, setPopConfirmVisibility] = React.useState(false);
  const [nextTab, setNextTab] = React.useState<string>(settingTabs[0].identification);

  React.useEffect(() => {
    loadSettings(activeTab.identification).then(() => setTabLoading(false));
  }, [activeTab.identification, loadSettings]);

  const changeTabs = async (activeKey: string) => {
    const tab = settingTabs.find((t) => t.identification === activeKey);
    if (tab) {
      if (tab.identification !== activeTab.identification) {
        if (hasUnsavedChanges(activeTab.identification)) {
          setPopConfirmVisibility(true);
          setNextTab(activeKey);
        } else {
          switchTabs(activeKey);
        }
      }
    }
  };

  const switchTabs = (activeKey: string) => {
    const tab = settingTabs.find((t) => t.identification === activeKey);
    if (tab) {
      setTabLoading(true);
      setActiveTab(tab);
    }
  };

  return (
    <StyledGrid gap="10px" columns={1} rows="auto 1fr">
      <Header>
        <StyledPageHeader
          title={<span data-testid="pageHeaderTitle">{title}</span>}
          onBack={() => null}
          backIcon={
            <BackButton
              {...activeTab}
              onGoBack={onGoBack}
              resetSettings={resetSettings}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          }
        >
          <StyledParagraph>{activeTab.tabText}</StyledParagraph>
        </StyledPageHeader>
      </Header>
      <Cell>
        <StyledTabs
          destroyInactiveTabPane
          tabBarStyle={{ marginBottom: 0 }}
          activeKey={activeTab.identification}
          onTabClick={changeTabs}
          data-testid="settingsTabs"
        >
          {settingTabs.map(({ identification, tabName, Content }) => (
            <TabPane
              key={identification}
              tab={
                <SettingsTabHeader
                  currentTabKey={activeTab.identification}
                  tabKey={nextTab}
                  tabName={tabName}
                  switchTabs={switchTabs}
                  visible={activeTab.identification === identification && popConfirmVisibility}
                  handleCancel={() => setPopConfirmVisibility(false)}
                  resetSettings={resetSettings}
                />
              }
            >
              {Content ? (
                tabLoading ? (
                  <Spinner />
                ) : (
                  <Content identification={identification} />
                )
              ) : (
                <p>{t('notYetImplemented')}</p>
              )}
            </TabPane>
          ))}
        </StyledTabs>
      </Cell>
    </StyledGrid>
  );
};
